import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type BtnVariant = 'primary' | 'ghost' | 'soft' | 'teal' | 'quiet' | 'bare';
export type BtnSize = 'sm' | 'md';

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

const VARIANT_STYLES: Record<BtnVariant, string> = {
  primary:
    'bg-coral text-white border border-coral hover:bg-coral-hover hover:border-coral-hover',
  ghost:
    'bg-card text-foreground border border-border hover:bg-warm-bg',
  soft:
    'bg-coral-light text-coral-dark border border-coral/20 hover:bg-coral-light/80',
  teal:
    'bg-teal text-white border border-teal hover:bg-teal-hover hover:border-teal-hover',
  quiet:
    'bg-transparent text-muted-foreground border border-transparent hover:bg-warm-bg hover:text-foreground',
  // `bare` mirrors the design's editor-ui.jsx Btn `bare` variant: transparent
  // background, transparent border, muted text. Visually equivalent to
  // `quiet`; kept as a distinct alias so callers can match the design's
  // variant vocabulary (editor-ui.jsx:71).
  bare:
    'bg-transparent text-muted-foreground border border-transparent hover:bg-warm-bg hover:text-foreground',
};

const SIZE_STYLES: Record<BtnSize, string> = {
  sm: 'h-7 px-2.5 text-[12px] gap-1.5 rounded-md',
  md: 'h-8 px-3 text-[13px] gap-1.5 rounded-md',
};

export const Btn = forwardRef<HTMLButtonElement, BtnProps>(
  ({ variant = 'ghost', size = 'md', icon, iconRight, className, children, type, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        className={cn(
          'inline-flex items-center font-medium whitespace-nowrap cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 transition-colors',
          VARIANT_STYLES[variant],
          SIZE_STYLES[size],
          className,
        )}
        {...rest}
      >
        {icon && <span className="inline-flex shrink-0">{icon}</span>}
        {children}
        {iconRight && <span className="inline-flex shrink-0">{iconRight}</span>}
      </button>
    );
  },
);
Btn.displayName = 'Btn';
